import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  iconCheckSmallStep3Courses,
  iconCheckStep3Courses,
  iconGroupSmallStep3Courses,
  iconQuizStep3Courses,
  iconSearchStep3Courses,
  iconUsersSmallStep3Courses,
} from '../../assets';
import { useDebounce } from '../../hooks/useDebounce';
import { useTheme } from '../../theme';

/** type/role/location são chaves i18n: courses.create.targeting_group_type_*, targeting_role_*, targeting_location_* */
export interface TargetingGroup {
  id: string;
  name: string;
  type: string;
  members: number;
  selected: boolean;
}

export interface TargetingUser {
  id: string;
  name: string;
  role: string;
  location: string;
  selected: boolean;
}

export interface CreateCourseStepThreeProps {
  /** Número de módulos (vindo do step 2 / CreateCoursePage) */
  modulesCount?: number;
  /** Número total de lições (vindo do step 2 / CreateCoursePage) */
  lessonsCount?: number;
  /** Grupos organizacionais (vindo da API/página; vazio por padrão) */
  initialGroups?: TargetingGroup[];
  /** Usuários individuais (vindo da API/página; vazio por padrão) */
  initialUsers?: TargetingUser[];
  /** Busca grupos via API (server-side) com paginação fixa em 5 itens por página. */
  fetchTargetingGroups?: (params: {
    page: number;
    search?: string;
  }) => Promise<{ data: TargetingGroup[]; total: number }>;
  /** Busca usuários via API (server-side) com paginação fixa em 5 itens por página. */
  fetchTargetingUsers?: (params: {
    page: number;
    search?: string;
  }) => Promise<{ data: TargetingUser[]; total: number }>;
  /** Notifica o container sempre que a seleção de grupos/usuários mudar. */
  onChangeTargeting?: (params: {
    userIds: string[];
    groupIds: string[];
  }) => void;
  onPublish?: () => void;
}

export function CreateCourseStepThree({
  modulesCount = 0,
  lessonsCount = 0,
  initialGroups = [],
  initialUsers = [],
  fetchTargetingGroups,
  fetchTargetingUsers,
  onChangeTargeting,
  onPublish: _onPublish,
}: CreateCourseStepThreeProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const sc = theme.palette.createCourse;

  const maskedIconPrimaryGreen = (
    src: string,
    sizePx: number
  ): React.CSSProperties => ({
    width: sizePx,
    height: sizePx,
    backgroundColor: theme.palette.text.primary,
    WebkitMaskImage: `url(${src})`,
    WebkitMaskSize: 'contain',
    WebkitMaskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskImage: `url(${src})`,
    maskSize: 'contain',
    maskRepeat: 'no-repeat',
    maskPosition: 'center',
    display: 'block',
  });
  const TARGETING_PAGE_SIZE = 5;
  const [groups, setGroups] = useState<TargetingGroup[]>(initialGroups);
  const [users, setUsers] = useState<TargetingUser[]>(initialUsers);
  /** IDs selecionados persistem entre paginação/refetch para não perder ao trocar de página ou buscar. */
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>(() =>
    initialGroups.filter(g => g.selected).map(g => g.id)
  );
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>(() =>
    initialUsers.filter(u => u.selected).map(u => u.id)
  );
  /** Detalhes dos itens selecionados para exibir nos chips e na lista (nome, type, members, etc.). */
  const [selectedGroupsDetail, setSelectedGroupsDetail] = useState<
    Record<string, { name: string; type?: string; members?: number }>
  >(() => {
    const o: Record<string, { name: string; type?: string; members?: number }> = {};
    initialGroups.filter(g => g.selected).forEach(g => (o[g.id] = { name: g.name, type: g.type, members: g.members }));
    return o;
  });
  const [selectedUsersDetail, setSelectedUsersDetail] = useState<
    Record<string, { name: string; role?: string; location?: string }>
  >(() => {
    const o: Record<string, { name: string; role?: string; location?: string }> = {};
    initialUsers.filter(u => u.selected).forEach(u => (o[u.id] = { name: u.name, role: u.role, location: u.location }));
    return o;
  });
  const [groupSearch, setGroupSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [groupSearchOpen, setGroupSearchOpen] = useState(false);
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  const groupSearchRef = useRef<HTMLDivElement>(null);
  const userSearchRef = useRef<HTMLDivElement>(null);
  const selectedGroupIdsRef = useRef(selectedGroupIds);
  const selectedUserIdsRef = useRef(selectedUserIds);
  selectedGroupIdsRef.current = selectedGroupIds;
  selectedUserIdsRef.current = selectedUserIds;

  const debouncedGroupSearch = useDebounce(groupSearch, 400);
  const debouncedUserSearch = useDebounce(userSearch, 400);
  const [groupPageIndex, setGroupPageIndex] = useState(0);
  const [userPageIndex, setUserPageIndex] = useState(0);
  const [groupTotal, setGroupTotal] = useState(
    fetchTargetingGroups ? 0 : initialGroups.length
  );
  const [userTotal, setUserTotal] = useState(
    fetchTargetingUsers ? 0 : initialUsers.length
  );
  const [groupLoading, setGroupLoading] = useState(false);
  const [userLoading, setUserLoading] = useState(false);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (groupSearchRef.current && !groupSearchRef.current.contains(target)) {
        setGroupSearchOpen(false);
      }
      if (userSearchRef.current && !userSearchRef.current.contains(target)) {
        setUserSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  const selectedGroupsCount = selectedGroupIds.length;
  const selectedUsersCount = selectedUserIds.length;
  const totalAssignments = selectedGroupsCount + selectedUsersCount;

  const groupPageCount = Math.max(
    1,
    Math.ceil(groupTotal / TARGETING_PAGE_SIZE) || 1
  );
  const userPageCount = Math.max(
    1,
    Math.ceil(userTotal / TARGETING_PAGE_SIZE) || 1
  );

  useEffect(() => {
    if (!onChangeTargeting) return;
    onChangeTargeting({
      userIds: [...selectedUserIds],
      groupIds: [...selectedGroupIds],
    });
  }, [selectedGroupIds, selectedUserIds, onChangeTargeting]);

  useEffect(() => {
    if (!fetchTargetingGroups) return;
    const load = async () => {
      setGroupLoading(true);
      try {
        const res = await fetchTargetingGroups({
          page: groupPageIndex + 1,
          search: debouncedGroupSearch.trim() || undefined,
        });
        const incoming = res.data ?? [];
        const selectedSet = new Set(selectedGroupIdsRef.current);
        setGroups(
          incoming.map(g => ({
            ...g,
            selected: selectedSet.has(g.id),
          }))
        );
        setGroupTotal(typeof res.total === 'number' ? res.total : incoming.length);
      } catch {
        setGroups([]);
        setGroupTotal(0);
      } finally {
        setGroupLoading(false);
      }
    };
    if (groupSearchOpen || debouncedGroupSearch.trim().length > 0) {
      load();
    }
  }, [
    fetchTargetingGroups,
    groupPageIndex,
    debouncedGroupSearch,
    groupSearchOpen,
  ]);

  useEffect(() => {
    if (!fetchTargetingUsers) return;
    const load = async () => {
      setUserLoading(true);
      try {
        const res = await fetchTargetingUsers({
          page: userPageIndex + 1,
          search: debouncedUserSearch.trim() || undefined,
        });
        const incoming = res.data ?? [];
        const selectedSet = new Set(selectedUserIdsRef.current);
        setUsers(
          incoming.map(u => ({
            ...u,
            selected: selectedSet.has(u.id),
          }))
        );
        setUserTotal(typeof res.total === 'number' ? res.total : incoming.length);
      } catch {
        setUsers([]);
        setUserTotal(0);
      } finally {
        setUserLoading(false);
      }
    };
    if (userSearchOpen || debouncedUserSearch.trim().length > 0) {
      load();
    }
  }, [
    fetchTargetingUsers,
    userPageIndex,
    debouncedUserSearch,
    userSearchOpen,
  ]);

  function getOrFallback(labelKey: string, rawValue: string): string {
    if (!rawValue) return '';
    const translated = t(labelKey);
    return translated === labelKey ? rawValue : translated;
  }

  const toggleGroup = (id: string) => {
    setSelectedGroupIds(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      return next;
    });
    setGroups(prev => {
      const has = prev.some(g => g.id === id);
      if (!has) return prev;
      return prev.map(g =>
        g.id === id ? { ...g, selected: !g.selected } : g
      );
    });
    setSelectedGroupsDetail(prev => {
      if (prev[id]) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      const g = groups.find(gr => gr.id === id);
      if (g) return { ...prev, [id]: { name: g.name, type: g.type, members: g.members } };
      return prev;
    });
  };

  const toggleUser = (id: string) => {
    setSelectedUserIds(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      return next;
    });
    setUsers(prev => {
      const has = prev.some(u => u.id === id);
      if (!has) return prev;
      return prev.map(u =>
        u.id === id ? { ...u, selected: !u.selected } : u
      );
    });
    setSelectedUsersDetail(prev => {
      if (prev[id]) {
        const next = { ...prev };
        delete next[id];
        return next;
      }
      const u = users.find(ur => ur.id === id);
      if (u) return { ...prev, [id]: { name: u.name, role: u.role, location: u.location } };
      return prev;
    });
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius.md,
    padding: theme.spacing.xl,
    marginBottom: theme.spacing.xl,
    boxShadow: 'none',
  };

  const headerRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.xl,
    flexWrap: 'wrap',
  };

  const headerLeftStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing.sm,
  };

  const iconBoxStyle: React.CSSProperties = {
    width: sc.iconBoxSize,
    height: sc.iconBoxSize,
    borderRadius: theme.shape.borderRadius.sm2,
    backgroundColor: sc.targeting.iconBoxBg,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const columnTitleWrapStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
  };

  /** Checkbox não marcado: borda cinza clara, fundo branco (como no print) */
  const checkboxWrapStyle: React.CSSProperties = {
    width: sc.checkboxSize,
    height: sc.checkboxSize,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.shape.borderRadius.xs,
    border: `${theme.shape.borderWidth.medium} solid ${theme.palette.border.input}`,
    backgroundColor: theme.palette.background.paper,
  };

  const checkboxWrapSelectedStyle: React.CSSProperties = {
    ...checkboxWrapStyle,
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main,
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: theme.typography.fontSize.xl,
    lineHeight: 1.25,
    color: theme.palette.text.primary,
    margin: 0,
  };

  const sectionSubtitleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing.xs,
    marginBottom: 0,
  };

  const countersWrapStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm2,
  };

  const counterBoxStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    padding: `${theme.spacing.xs} ${theme.spacing.sm2}`,
    borderRadius: theme.shape.borderRadius.sm2,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.medium,
    color: theme.palette.text.primary,
  };

  const counterBoxGroupsStyle: React.CSSProperties = {
    ...counterBoxStyle,
    backgroundColor: sc.targeting.counterBlueBg,
    border: `1px solid ${sc.targeting.counterBlueBorder}`,
  };

  const counterBoxUsersStyle: React.CSSProperties = {
    ...counterBoxStyle,
    backgroundColor: sc.targeting.counterPurpleBg,
    border: `1px solid ${sc.targeting.counterPurpleBorder}`,
  };

  const assignToRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.md,
    flexWrap: 'wrap',
  };

  const assignSectionTitleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: theme.typography.fontSize.xl,
    color: theme.palette.text.primary,
    margin: 0,
  };

  const selectedAssignmentsBoxStyle: React.CSSProperties = {
    padding: theme.spacing.md,
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: theme.shape.borderRadius.sm2,
    backgroundColor: theme.palette.background.paper,
    marginBottom: theme.spacing.lg,
  };

  const selectedAssignmentsTitleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: theme.typography.fontSize.sm,
    color: theme.palette.text.primary,
    margin: 0,
    marginBottom: theme.spacing.xs,
  };

  const selectedAssignmentsEmptyStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    color: theme.palette.text.secondary,
    margin: 0,
  };

  const selectedChipsWrapStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing.sm,
    marginTop: theme.spacing.xs,
  };

  const chipGroupStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    padding: `${theme.spacing.xs} ${theme.spacing.sm2}`,
    borderRadius: theme.shape.borderRadius.sm,
    backgroundColor: sc.targeting.counterBlueBg,
    border: `1px solid ${sc.targeting.counterBlueBorder}`,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    color: theme.palette.text.primary,
  };

  const chipUserStyle: React.CSSProperties = {
    ...chipGroupStyle,
    backgroundColor: sc.targeting.counterPurpleBg,
    border: `1px solid ${sc.targeting.counterPurpleBorder}`,
  };

  const chipRemoveStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    padding: 0,
    border: 'none',
    borderRadius: '50%',
    backgroundColor: 'transparent',
    color: theme.palette.text.secondary,
    cursor: 'pointer',
    fontSize: 14,
    lineHeight: 1,
  };

  const columnsWrapStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing.lg,
  };

  const columnStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.sm,
  };

  const columnTitleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: theme.typography.fontSize.base,
    color: theme.palette.text.primary,
    margin: 0,
  };

  const searchWrapStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%',
  };

  const searchIconStyle: React.CSSProperties = {
    position: 'absolute',
    left: theme.spacing.sm2,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
  };

  const searchInputStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    height: sc.iconBoxSize,
    padding: theme.spacing.sm2,
    paddingLeft: `calc(${theme.spacing.sm2} + ${sc.searchIconOffset}px)`,
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default,
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: theme.shape.borderRadius.sm,
    outline: 'none',
  };

  /** Overlay do search: bloco cinza que aparece ao digitar (resultados da busca) */
  const searchResultsOverlayStyle: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    right: 0,
    top: '100%',
    marginTop: theme.spacing.xs,
    backgroundColor: sc.targeting.searchResultsBg,
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: theme.shape.borderRadius.sm,
    boxShadow: sc.dropdownBoxShadow,
    maxHeight: sc.searchResultsMaxHeight,
    overflowY: 'auto',
    zIndex: 1000,
  };

  /** Lista de itens já selecionados (fica atrás do overlay quando ele está aberto) */
  const selectedListWrapStyle: React.CSSProperties = {
    marginTop: theme.spacing.sm,
    maxHeight: sc.searchResultsMaxHeight,
    overflowY: 'auto',
  };

  const listStyle: React.CSSProperties = {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.sm,
  };

  /** Linha da lista de resultados do search no overlay (fundo cinza claro) */
  const itemStyle: React.CSSProperties = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    padding: theme.spacing.sm2,
    backgroundColor: theme.palette.input.background,
    border: `1px solid ${theme.palette.border.main}`,
    borderRadius: theme.shape.borderRadius.sm2,
    cursor: 'pointer',
  };

  /** Linha da lista de itens selecionados (card branco atrás do overlay) */
  const selectedItemStyle: React.CSSProperties = {
    ...itemStyle,
    backgroundColor: theme.palette.background.paper,
  };

  const itemTextStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    color: theme.palette.text.primary,
    margin: 0,
  };

  /** Nome em negrito na linha da lista (como no print) */
  const itemTitleStyle: React.CSSProperties = {
    ...itemTextStyle,
    fontWeight: theme.typography.fontWeight.bold,
  };

  const itemSubtextStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.xs,
    color: theme.palette.text.secondary,
    margin: 0,
  };

  const publishBarStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing.lg,
    padding: theme.spacing.lg,
    backgroundColor: theme.palette.text.primary,
    borderRadius: theme.shape.borderRadius.md,
    border: 'none',
  };

  const publishBarLeftStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
  };

  const publishBarTitleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: theme.typography.fontSize.lg,
    color: theme.palette.text.white,
    margin: 0,
  };

  const publishBarSubtitleStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.sm,
    color: theme.palette.text.white,
    margin: 0,
    opacity: 0.9,
  };

  const publishBarMetricsStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xl,
  };

  const publishBarMetricStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing.xs,
  };

  const publishBarMetricValueStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: theme.typography.fontSize.lg,
    color: theme.palette.text.white,
    margin: 0,
  };

  const publishBarMetricLabelStyle: React.CSSProperties = {
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.fontSize.xs,
    color: theme.palette.text.white,
    margin: 0,
    opacity: 0.9,
  };

  const publishBarMetricLabelWithIconStyle: React.CSSProperties = {
    ...publishBarMetricLabelStyle,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
  };

  const filteredGroups =
    fetchTargetingGroups || !groupSearch
      ? groups
      : groups.filter(
          g =>
            g.name.toLowerCase().includes(groupSearch.toLowerCase()) ||
            g.type.toLowerCase().includes(groupSearch.toLowerCase())
        );

  const filteredUsers =
    fetchTargetingUsers || !userSearch
      ? users
      : users.filter(
          u =>
            u.name.toLowerCase().includes(userSearch.toLowerCase()) ||
            u.role.toLowerCase().includes(userSearch.toLowerCase()) ||
            u.location.toLowerCase().includes(userSearch.toLowerCase())
        );

  return (
    <>
      <div style={cardStyle}>
        <header style={headerRowStyle}>
          <div style={headerLeftStyle}>
            <div style={iconBoxStyle} aria-hidden>
              <div
                style={maskedIconPrimaryGreen(
                  iconQuizStep3Courses,
                  sc.iconSize
                )}
              />
            </div>
            <div>
              <h2 style={sectionTitleStyle}>
                {t('courses.create.step_targeting')}
              </h2>
              <p style={sectionSubtitleStyle}>
                {t('courses.create.targeting_subtitle')}
              </p>
            </div>
          </div>
        </header>

        <div style={assignToRowStyle}>
          <h3 style={assignSectionTitleStyle}>
            {t('courses.create.targeting_assign_to')}
          </h3>
          <div style={countersWrapStyle}>
            <div style={counterBoxGroupsStyle}>
              <img
                src={iconGroupSmallStep3Courses}
                alt=""
                width={sc.iconSizeSmall}
                height={sc.iconSizeSmall}
              />
              {t('courses.create.targeting_groups_count', {
                count: selectedGroupsCount,
              })}
            </div>
            <div style={counterBoxUsersStyle}>
              <img
                src={iconUsersSmallStep3Courses}
                alt=""
                width={sc.iconSizeSmall}
                height={sc.iconSizeSmall}
              />
              {t('courses.create.targeting_users_count', {
                count: selectedUsersCount,
              })}
            </div>
          </div>
        </div>

        <div style={selectedAssignmentsBoxStyle}>
          <p style={selectedAssignmentsTitleStyle}>
            {t('courses.create.targeting_selected_assignments')}
          </p>
          {totalAssignments === 0 ? (
            <p style={selectedAssignmentsEmptyStyle}>
              {t('courses.create.targeting_no_assignments_yet')}
            </p>
          ) : (
            <div style={selectedChipsWrapStyle}>
              {selectedGroupIds.map(groupId => (
                <span key={groupId} style={chipGroupStyle}>
                  <img
                    src={iconGroupSmallStep3Courses}
                    alt=""
                    width={16}
                    height={16}
                  />
                  <span>{selectedGroupsDetail[groupId]?.name ?? groupId}</span>
                  <button
                    type="button"
                    style={chipRemoveStyle}
                    onClick={e => {
                      e.stopPropagation();
                      toggleGroup(groupId);
                    }}
                    aria-label={`${t('courses.create.targeting_remove')} ${selectedGroupsDetail[groupId]?.name ?? groupId}`}
                  >
                    ×
                  </button>
                </span>
              ))}
              {selectedUserIds.map(userId => (
                <span key={userId} style={chipUserStyle}>
                  <img
                    src={iconUsersSmallStep3Courses}
                    alt=""
                    width={16}
                    height={16}
                  />
                  <span>{selectedUsersDetail[userId]?.name ?? userId}</span>
                  <button
                    type="button"
                    style={chipRemoveStyle}
                    onClick={e => {
                      e.stopPropagation();
                      toggleUser(userId);
                    }}
                    aria-label={`${t('courses.create.targeting_remove')} ${selectedUsersDetail[userId]?.name ?? userId}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div style={columnsWrapStyle}>
          <div style={columnStyle}>
            <h4 style={columnTitleStyle}>
              <span style={columnTitleWrapStyle}>
                <img
                  src={iconGroupSmallStep3Courses}
                  alt=""
                  width={sc.iconSizeSmall}
                  height={sc.iconSizeSmall}
                />
                {t('courses.create.targeting_organizational_groups')}
              </span>
            </h4>
            <div style={searchWrapStyle} ref={groupSearchRef}>
              <span style={searchIconStyle} aria-hidden>
                <img
                  src={iconSearchStep3Courses}
                  alt=""
                  width={18}
                  height={18}
                />
              </span>
              <input
                type="search"
                value={groupSearch}
                onChange={e => {
                  setGroupSearch(e.target.value);
                  setGroupPageIndex(0);
                }}
                onFocus={() => {
                  setGroupSearchOpen(true);
                  if (fetchTargetingGroups && !groups.length) {
                    setGroupPageIndex(0);
                  }
                }}
                placeholder={t('courses.create.targeting_search_placeholder')}
                aria-label={t('courses.create.targeting_search_aria')}
                style={searchInputStyle}
              />
              {(groupSearchOpen || groupSearch.trim().length > 0) && (
                <div style={searchResultsOverlayStyle}>
                  <ul style={{ ...listStyle, padding: theme.spacing.sm }}>
                    {filteredGroups.map(group => (
                      <li
                        key={group.id}
                        style={itemStyle}
                        onClick={() => toggleGroup(group.id)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleGroup(group.id);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <input
                          type="checkbox"
                          checked={group.selected}
                          onChange={() => toggleGroup(group.id)}
                          onClick={e => e.stopPropagation()}
                          aria-label={`${group.name}, ${getOrFallback(
                            'courses.create.targeting_group_type_' + group.type,
                            group.type
                          )}, ${t('courses.create.targeting_mock_members', { count: group.members })}`}
                          style={{
                            position: 'absolute',
                            opacity: 0,
                            width: 0,
                            height: 0,
                            margin: 0,
                            padding: 0,
                          }}
                        />
                        <div
                          style={
                            group.selected
                              ? checkboxWrapSelectedStyle
                              : checkboxWrapStyle
                          }
                          aria-hidden
                        >
                          {group.selected && (
                            <img
                              src={iconCheckSmallStep3Courses}
                              alt=""
                              width={sc.checkboxSize - 4}
                              height={sc.checkboxSize - 4}
                            />
                          )}
                        </div>
                        <div>
                          <p style={itemTitleStyle}>{group.name}</p>
                          <p style={itemSubtextStyle}>
                            {getOrFallback(
                              'courses.create.targeting_group_type_' + group.type,
                              group.type
                            )}{' '}
                            •{' '}
                            {t('courses.create.targeting_mock_members', {
                              count: group.members,
                            })}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {fetchTargetingGroups && groupTotal > 0 && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: theme.spacing.sm,
                        padding: theme.spacing.sm,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setGroupPageIndex(p => Math.max(0, p - 1))
                        }
                        disabled={groupPageIndex <= 0 || groupLoading}
                        style={{
                          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                          borderRadius: theme.shape.borderRadius.sm,
                          border: `1px solid ${theme.palette.border.main}`,
                          backgroundColor: theme.palette.background.paper,
                          cursor:
                            groupPageIndex <= 0 || groupLoading
                              ? 'not-allowed'
                              : 'pointer',
                          opacity:
                            groupPageIndex <= 0 || groupLoading ? 0.5 : 1,
                        }}
                      >
                        {t('questionBank.previous')}
                      </button>
                      <span
                        style={{
                          fontFamily: theme.typography.fontFamily,
                          fontSize: theme.typography.fontSize.sm,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {t('questionBank.pagination_summary', {
                          count: filteredGroups.length,
                          total: groupTotal,
                        })}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setGroupPageIndex(p =>
                            p + 1 >= groupPageCount ? p : p + 1
                          )
                        }
                        disabled={
                          groupPageIndex >= groupPageCount - 1 || groupLoading
                        }
                        style={{
                          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                          borderRadius: theme.shape.borderRadius.sm,
                          border: `1px solid ${theme.palette.border.main}`,
                          backgroundColor: theme.palette.background.paper,
                          cursor:
                            groupPageIndex >= groupPageCount - 1 ||
                            groupLoading
                              ? 'not-allowed'
                              : 'pointer',
                          opacity:
                            groupPageIndex >= groupPageCount - 1 ||
                            groupLoading
                              ? 0.5
                              : 1,
                        }}
                      >
                        {t('questionBank.next')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            {selectedGroupsCount > 0 && (
              <div style={selectedListWrapStyle}>
                <ul style={listStyle}>
                  {selectedGroupIds.map(groupId => {
                    const d = selectedGroupsDetail[groupId];
                    const name = d?.name ?? groupId;
                    const type = d?.type ?? '';
                    const members = d?.members ?? 0;
                    return (
                      <li
                        key={groupId}
                        style={selectedItemStyle}
                        onClick={() => toggleGroup(groupId)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleGroup(groupId);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <input
                          type="checkbox"
                          checked
                          readOnly
                          aria-label={name}
                          style={{
                            position: 'absolute',
                            opacity: 0,
                            width: 0,
                            height: 0,
                            margin: 0,
                            padding: 0,
                          }}
                        />
                        <div style={checkboxWrapSelectedStyle} aria-hidden>
                          <img
                            src={iconCheckSmallStep3Courses}
                            alt=""
                            width={sc.checkboxSize - 4}
                            height={sc.checkboxSize - 4}
                          />
                        </div>
                        <div>
                          <p style={itemTitleStyle}>{name}</p>
                          <p style={itemSubtextStyle}>
                            {getOrFallback('courses.create.targeting_group_type_' + type, type)} •{' '}
                            {t('courses.create.targeting_mock_members', { count: members })}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>

          <div style={columnStyle}>
            <h4 style={columnTitleStyle}>
              <span style={columnTitleWrapStyle}>
                <img
                  src={iconUsersSmallStep3Courses}
                  alt=""
                  width={sc.iconSizeSmall}
                  height={sc.iconSizeSmall}
                />
                {t('courses.create.targeting_individual_users')}
              </span>
            </h4>
            <div style={searchWrapStyle} ref={userSearchRef}>
              <span style={searchIconStyle} aria-hidden>
                <img
                  src={iconSearchStep3Courses}
                  alt=""
                  width={18}
                  height={18}
                />
              </span>
              <input
                type="search"
                value={userSearch}
                onChange={e => {
                  setUserSearch(e.target.value);
                  setUserPageIndex(0);
                }}
                onFocus={() => {
                  setUserSearchOpen(true);
                  if (fetchTargetingUsers && !users.length) {
                    setUserPageIndex(0);
                  }
                }}
                placeholder={t('courses.create.targeting_search_placeholder')}
                aria-label={t('courses.create.targeting_search_aria')}
                style={searchInputStyle}
              />
              {(userSearchOpen || userSearch.trim().length > 0) && (
                <div style={searchResultsOverlayStyle}>
                  <ul style={{ ...listStyle, padding: theme.spacing.sm }}>
                    {filteredUsers.map(user => (
                      <li
                        key={user.id}
                        style={itemStyle}
                        onClick={() => toggleUser(user.id)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleUser(user.id);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <input
                          type="checkbox"
                          checked={user.selected}
                          onChange={() => toggleUser(user.id)}
                          onClick={e => e.stopPropagation()}
                          aria-label={`${user.name}, ${getOrFallback(
                            'courses.create.targeting_role_' + user.role,
                            user.role
                          )}, ${getOrFallback(
                            'courses.create.targeting_location_' + user.location,
                            user.location
                          )}`}
                          style={{
                            position: 'absolute',
                            opacity: 0,
                            width: 0,
                            height: 0,
                            margin: 0,
                            padding: 0,
                          }}
                        />
                        <div
                          style={
                            user.selected
                              ? checkboxWrapSelectedStyle
                              : checkboxWrapStyle
                          }
                          aria-hidden
                        >
                          {user.selected && (
                            <img
                              src={iconCheckSmallStep3Courses}
                              alt=""
                              width={sc.checkboxSize - 4}
                              height={sc.checkboxSize - 4}
                            />
                          )}
                        </div>
                        <div>
                          <p style={itemTitleStyle}>{user.name}</p>
                          <p style={itemSubtextStyle}>
                            {getOrFallback(
                              'courses.create.targeting_role_' + user.role,
                              user.role
                            )}{' '}
                            •{' '}
                            {getOrFallback(
                              'courses.create.targeting_location_' +
                                user.location,
                              user.location
                            )}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {fetchTargetingUsers && userTotal > 0 && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: theme.spacing.sm,
                        padding: theme.spacing.sm,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setUserPageIndex(p => Math.max(0, p - 1))
                        }
                        disabled={userPageIndex <= 0 || userLoading}
                        style={{
                          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                          borderRadius: theme.shape.borderRadius.sm,
                          border: `1px solid ${theme.palette.border.main}`,
                          backgroundColor: theme.palette.background.paper,
                          cursor:
                            userPageIndex <= 0 || userLoading
                              ? 'not-allowed'
                              : 'pointer',
                          opacity:
                            userPageIndex <= 0 || userLoading ? 0.5 : 1,
                        }}
                      >
                        {t('questionBank.previous')}
                      </button>
                      <span
                        style={{
                          fontFamily: theme.typography.fontFamily,
                          fontSize: theme.typography.fontSize.sm,
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {t('questionBank.pagination_summary', {
                          count: filteredUsers.length,
                          total: userTotal,
                        })}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setUserPageIndex(p =>
                            p + 1 >= userPageCount ? p : p + 1
                          )
                        }
                        disabled={
                          userPageIndex >= userPageCount - 1 || userLoading
                        }
                        style={{
                          padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                          borderRadius: theme.shape.borderRadius.sm,
                          border: `1px solid ${theme.palette.border.main}`,
                          backgroundColor: theme.palette.background.paper,
                          cursor:
                            userPageIndex >= userPageCount - 1 ||
                            userLoading
                              ? 'not-allowed'
                              : 'pointer',
                          opacity:
                            userPageIndex >= userPageCount - 1 ||
                            userLoading
                              ? 0.5
                              : 1,
                        }}
                      >
                        {t('questionBank.next')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            {selectedUsersCount > 0 && (
              <div style={selectedListWrapStyle}>
                <ul style={listStyle}>
                  {selectedUserIds.map(userId => {
                    const d = selectedUsersDetail[userId];
                    const name = d?.name ?? userId;
                    const role = d?.role ?? '';
                    const location = d?.location ?? '';
                    return (
                      <li
                        key={userId}
                        style={selectedItemStyle}
                        onClick={() => toggleUser(userId)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleUser(userId);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <input
                          type="checkbox"
                          checked
                          readOnly
                          aria-label={name}
                          style={{
                            position: 'absolute',
                            opacity: 0,
                            width: 0,
                            height: 0,
                            margin: 0,
                            padding: 0,
                          }}
                        />
                        <div style={checkboxWrapSelectedStyle} aria-hidden>
                          <img
                            src={iconCheckSmallStep3Courses}
                            alt=""
                            width={sc.checkboxSize - 4}
                            height={sc.checkboxSize - 4}
                          />
                        </div>
                        <div>
                          <p style={itemTitleStyle}>{name}</p>
                          <p style={itemSubtextStyle}>
                            {getOrFallback('courses.create.targeting_role_' + role, role)} •{' '}
                            {getOrFallback('courses.create.targeting_location_' + location, location)}
                          </p>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={publishBarStyle}>
        <div style={publishBarLeftStyle}>
          <h3 style={publishBarTitleStyle}>
            {t('courses.create.targeting_publish_course')}
          </h3>
          <p style={publishBarSubtitleStyle}>
            {t('courses.create.targeting_ready_to_publish')}
          </p>
          <p style={publishBarSubtitleStyle}>
            {t('courses.create.targeting_course_available_to', {
              count: totalAssignments,
            })}
          </p>
        </div>
        <div style={publishBarMetricsStyle}>
          <div style={publishBarMetricStyle}>
            <span style={publishBarMetricValueStyle}>{modulesCount}</span>
            <span style={publishBarMetricLabelStyle}>
              {t('courses.create.targeting_modules')}
            </span>
          </div>
          <div style={publishBarMetricStyle}>
            <span style={publishBarMetricValueStyle}>{lessonsCount}</span>
            <span style={publishBarMetricLabelWithIconStyle}>
              {t('courses.create.targeting_lessons')}
            </span>
          </div>
          <div style={publishBarMetricStyle}>
            <span style={publishBarMetricValueStyle}>0</span>
            <span style={publishBarMetricLabelStyle}>
              {t('courses.create.targeting_assignments')}
            </span>
          </div>
        </div>
        <img src={iconCheckStep3Courses} alt="" width={40} height={40} />
      </div>
    </>
  );
}
